import { type ColumnDef } from '@oysterjs/ui/data-table';
import {
  type GetWorkflowExecutionsQuery,
  WorkflowExecutionState,
  WorkflowTaskState
} from '~/api/graphql';
import clsx from 'clsx';

export type WorkflowExecution = GetWorkflowExecutionsQuery['workflowExecutions'][number];
export type ExecutionSortKey = 'state' | 'date' | 'tasks';
export type ExecutionGroupKey = never;

function stateLabel(state: WorkflowExecutionState): { text: string; className: string } {
  switch (state) {
    case WorkflowExecutionState.Running:
      return {
        text: 'Running',
        className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
      };
    case WorkflowExecutionState.Completed:
      return {
        text: 'Completed',
        className: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
      };
    case WorkflowExecutionState.Failed:
      return {
        text: 'Failed',
        className: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
      };
    case WorkflowExecutionState.Cancelled:
      return {
        text: 'Cancelled',
        className: 'bg-neutral-100 text-neutral-600 dark:bg-neutral-700 dark:text-neutral-300'
      };
    case WorkflowExecutionState.Queued:
    default:
      return {
        text: 'Queued',
        className: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
      };
  }
}

function formatDuration(start?: Date | null, end?: Date | null): string | null {
  if (!start || !end) return null;
  const ms = new Date(end).getTime() - new Date(start).getTime();
  if (ms < 0) return null;
  if (ms < 1000) return `${ms}ms`;
  const totalSeconds = Math.round(ms / 1000);
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes < 60) return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
}

export function getExecutionColumns(): ColumnDef<
  WorkflowExecution,
  ExecutionSortKey,
  ExecutionGroupKey
>[] {
  return [
    {
      id: 'state',
      header: 'Status',
      sortKey: 'state',
      cellClassName: 'w-28',
      renderCell: (exec) => {
        const s = stateLabel(exec.state);
        return (
          <span
            className={clsx(
              'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
              s.className
            )}
          >
            {exec.state === WorkflowExecutionState.Running && (
              <span className="mr-1.5 h-1.5 w-1.5 rounded-full bg-blue-500 animate-pulse" />
            )}
            {s.text}
          </span>
        );
      }
    },
    {
      id: 'id',
      header: 'Execution',
      renderCell: (exec) => (
        <span
          className="block truncate text-sm font-mono text-neutral-700 dark:text-neutral-300"
          title={exec.id}
        >
          {exec.id.slice(0, 16)}…
        </span>
      )
    },
    {
      id: 'tasks',
      header: 'Tasks',
      sortKey: 'tasks',
      cellClassName: 'w-32',
      renderCell: (exec) => {
        const total = exec.tasks.length;
        const completed = exec.tasks.filter((t) => t.state === WorkflowTaskState.Completed).length;
        return (
          <span className="text-sm text-neutral-600 dark:text-neutral-400">
            {completed}/{total} complete
          </span>
        );
      }
    },
    {
      id: 'duration',
      header: 'Duration',
      cellClassName: 'w-28',
      renderCell: (exec) => {
        const dur = formatDuration(exec.startedAt, exec.completedAt);
        return (
          <span className="text-sm text-neutral-500 dark:text-neutral-400">
            {dur ?? (exec.state === WorkflowExecutionState.Running ? '…' : '—')}
          </span>
        );
      }
    },
    {
      id: 'date',
      header: 'Created',
      sortKey: 'date',
      renderCell: (exec) => (
        <span className="block truncate text-xs text-neutral-500 dark:text-neutral-400">
          {new Intl.DateTimeFormat('en-US', { dateStyle: 'medium', timeStyle: 'short' }).format(
            new Date(exec.createdAt)
          )}
        </span>
      )
    }
  ];
}

export function executionSortFn(
  data: WorkflowExecution[],
  sortKey: ExecutionSortKey,
  direction: 'asc' | 'desc'
): WorkflowExecution[] {
  const sorted = [...data].sort((a, b) => {
    if (sortKey === 'state') return a.state.localeCompare(b.state);
    if (sortKey === 'tasks') {
      const ac = a.tasks.filter((t) => t.state === WorkflowTaskState.Completed).length;
      const bc = b.tasks.filter((t) => t.state === WorkflowTaskState.Completed).length;
      return ac - bc;
    }
    const aDate = a.createdAt ? new Date(a.createdAt).getTime() : 0;
    const bDate = b.createdAt ? new Date(b.createdAt).getTime() : 0;
    return aDate - bDate;
  });

  const reverse =
    (sortKey === 'date' && direction === 'asc') || (sortKey !== 'date' && direction === 'desc');
  return reverse ? [...sorted].reverse() : sorted;
}
